
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";

// GraphQL
import GET_POSTING from "@/apollo/jobboard/GetPosting.gql";
import DELETE_POSTING from "@/apollo/jobboard/DeletePosting.gql";
import DELETE_POSTING_INDUSTRIES from "@/apollo/industries/DeletePostingIndustries.gql";
import DELETE_POSTING_SKILLS from "@/apollo/skills/DeletePostingSkills.gql";
import DELETE_APPLICATIONS from "@/apollo/applications/DeleteContractorApplications.gql";

import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";

import PostingHeader from "@/components/postings/PostingHeaderV2.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import TabsWithRoutes from "@/components/common/TabsWithRoutesNew.vue";
import TabNotificationBubble from "@/components/common/TabNotificationBubble.vue";

// Interface
import RouteTabs from "@/entities/RouteTabs";
import RouteNames from "@/entities/RouteNames";
import Message, { MessageDuration } from "@/data/notificationsMessages";
import { NOTIFICATION } from "@/entities/Events";
import {
  Company,
  Posting,
  User,
  Application,
} from "@/generated/graphql";
import { ApplicantStatus } from "@/entities/ApplicantStatus";
import Roles from "@/entities/Roles";

@Component({
  components: {
    LoadingOverlay,
    PostingHeader,
    TabsWithRoutes,
    TabNotificationBubble,
  },
  apollo: {
    posting: {
      query: GET_POSTING,
      variables() {
        return {
          id: this.$route.params.id,
          companyId: this.company.id,
        };
      },
      skip() {
        return this.skipQuery;
      },
      update: (data) => data.posting_by_pk,
    },
  },
})
export default class PostingPage extends mixins(GetRelativeRouteNameMixin) {
  @Prop(Object) readonly company!: Company;
  @Prop({ required: true, type: Object }) readonly currentUser!: User;

  posting?: Posting = undefined;
  routes = RouteNames;
  routeTabs: RouteTabs = {
    [this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_DETAILS)]: "details",
    [this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_APPLICATIONS)]: "All applications",
    [this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_REVIEW)]: "under review",
    [this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_APPROVED)]: "approved",
    [this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_DECLINED)]: "declined",
    [this.getRelativeRouteName(RouteNames.RECRUITING_POSTING_CANCELLED)]: "withdrawn",
  };

  created() {
    if (!!this.company) {
      this.$apollo.queries.posting.skip = false;
    }
  }
    get isManager(): boolean {
      return this.currentUser.role === Roles.MANAGER;
    }
  get managerId(): string | undefined {
    return this.currentUser.manager?.id;
  }

  get showPostingSection(): boolean {
    return this.company && !!this.posting;
  }

  get activeApplications(): Application[] {
    return this.posting?.applications
      .filter((application) => {
        return !application.is_cancelled ||
                application.is_cancelled && application.status !== ApplicantStatus.NOT_REVIEWED;
      }) || [];
  }

  get allApplicantsNumber() {
    const count = this.posting?.applications.length
    if(!count)
      return;
    return count;
  }

  get approvedApplicantsNumber(): string {
     const posting = this.posting as any
    const count = posting?.approved?.aggregate?.count || ''
    if(!count)
      return ''
    return String(count);
  }

  get declinedApplicantsNumber(): string {
    const posting = this.posting as any
    const count = posting?.declined?.aggregate?.count || ''
    if(!count)
      return ''
    return String(count);
  }
  get getWithdrawNumbers(): string {
    const posting = this.posting as any
    const count = posting?.cancelled?.aggregate?.count || ''
    if(!count)
      return ''
    return String(count);
  }

  get underReviewApplicantsNumber(): string {
    const posting = this.posting as any
    const count = posting?.under_review_v2?.aggregate?.count || ''
    if(!count)
      return ''
    return String(count);
  }

  async removePosting() {
    try {
      await this._deletePostingSkills();
      await this._deletePostingIndustries();
      await this._deleteApplications();

      const { data } = await this._deletePosting();
      const deletedName = data.delete_posting.returning[0].job_title;

      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(
          `The '${deletedName}' job posting was successfully deleted.`,
          MessageDuration.MEDIUM
        )
      );

      this.$router.push({
        name: RouteNames.RECRUITING_POSTINGS,
      });
    } catch (e) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(e.message || 'Some unknown error occured.')
      );
    }
  }

  async _deletePosting() {
    return await this.$apollo.mutate({
      mutation: DELETE_POSTING,
      variables: {
        where: { id: { _eq: this.posting?.id } },
      },
    });
  }

  async _deleteApplications() {
    return await this.$apollo.mutate({
      mutation: DELETE_APPLICATIONS,
      variables: {
        where: { posting_id: { _eq: this.posting?.id } },
      },
    });
  }

  async _deletePostingIndustries() {
    return await this.$apollo.mutate({
      mutation: DELETE_POSTING_INDUSTRIES,
      variables: {
        where: { posting_id: { _eq: this.posting?.id } },
      },
    });
  }

  async _deletePostingSkills() {
    return await this.$apollo.mutate({
      mutation: DELETE_POSTING_SKILLS,
      variables: {
        where: { posting_id: { _eq: this.posting?.id } },
      },
    });
  }

  handleRefetch(applicationsNumber: number): void {
    if (!applicationsNumber) {
      this.$apollo.queries.posting.refetch();
      return;
    }
    if (applicationsNumber !== this.activeApplications.length) {
      this.$apollo.queries.posting.refetch();
    }
  }
}
